import { FC, useMemo } from "react";
import ProjectSections from "./ProjectSection";
import BarChart from "../../components/BarChart";
import { getFormatDate } from "../../pages/common/dateUtil";

type props = {
  neoJobsStat: any;
  neoProjStat: any;
  startDt: string;
  endDt: string;
};

// ──────────────────────────────────────────────
// 1) 시작일~종료일(포함) 날짜 시퀀스 생성
// ──────────────────────────────────────────────
const buildDateSeries = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return [] as { name: string; value: number }[];

  const start = new Date(startDate);
  const end = new Date(endDate);

  // 타임존/시간 영향 제거
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const out: { name: string; value: number }[] = [];
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setDate(d.getDate() + 1)) {
    out.push({
      name: getFormatDate(d, "YYYY-MM-DD"),
      value: 0,
    });
  }
  return out;
};

// ──────────────────────────────────────────────
// 2) 기본 시퀀스에 API 데이터 병합 (중복 push 금지)
//    - 같은 name(날짜)은 1개만 유지
//    - 범위 밖 날짜가 오면 포함(필요 시 제외 로직으로 바꿀 수 있음)
// ──────────────────────────────────────────────
const mergeCounts = (
  base: { name: string; value: number }[],
  apiList?: { date: string; count: number }[]
) => {
  const map = new Map<string, number>(base.map((r) => [r.name, r.value]));
  for (const it of apiList ?? []) {
    const key = String(it.date);
    const cnt = Number(it.count) || 0;
    map.set(key, cnt); // 덮어쓰기(누적이 필요하면 map.set(key, (map.get(key) ?? 0) + cnt))
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, value]) => ({ name, value }));
};

const ProjectManage7: FC<props> = ({ neoJobsStat, neoProjStat, startDt, endDt }) => {
  const width = "100%";
  const height = 400;

  // 기본 날짜 축 (포함 범위)
  const baseSeries = useMemo(() => buildDateSeries(startDt, endDt), [startDt, endDt]);

  // API 데이터
  const chart1_data = neoJobsStat?.resultList1 as { date: string; count: number }[] | undefined; // 객체 탐지(작업)
  const chart2_data = neoJobsStat?.resultList2 as { date: string; count: number }[] | undefined; // 객체 삭제(작업)
  const chart3_data = neoProjStat?.resultList1 as { date: string; count: number }[] | undefined; // 객체 탐지(프로젝트)
  const chart4_data = neoProjStat?.resultList2 as { date: string; count: number }[] | undefined; // 객체 삭제(프로젝트)

  // 병합 완료 데이터 (중복 없음)
  const projectDat1 = useMemo(() => mergeCounts(baseSeries, chart1_data), [baseSeries, chart1_data]);
  const projectDat2 = useMemo(() => mergeCounts(baseSeries, chart2_data), [baseSeries, chart2_data]);
  const projectDat3 = useMemo(() => mergeCounts(baseSeries, chart3_data), [baseSeries, chart3_data]);
  const projectDat4 = useMemo(() => mergeCounts(baseSeries, chart4_data), [baseSeries, chart4_data]);

  return (
    <ProjectSections title="사용 서비스" className="col-12 col-lg-12 col-md-12 user-state">
      <div style={{ display: "flex" }}>
        <div style={{ width: "50%" }}>
          <b>객체 탐지 (프로젝트)</b>
          <BarChart width={width} height={height} data={projectDat3} />
        </div>

        <div style={{ width: "50%" }}>
          <b>객체 탐지 (작업)</b>
          <BarChart width={width} height={height} data={projectDat1} />
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <div style={{ width: "50%" }}>
          <b>객체 삭제 (프로젝트)</b>
          <BarChart width={width} height={height} data={projectDat4} />
        </div>

        <div style={{ width: "50%" }}>
          <b>객체 삭제 (작업)</b>
          <BarChart width={width} height={height} data={projectDat2} />
        </div>
      </div>
    </ProjectSections>
  );
};

export default ProjectManage7;
